'use client'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { sanitize } from '@/lib/helpers/sanitize'
import { ExtraTechnicalSpecificationItem } from '@/types/product-detail'
import Image from 'next/image'

interface ProductDescriptionTechnicalSpecificationsProps {
  items?: ExtraTechnicalSpecificationItem[]
}

export const ProductDescriptionTechnicalSpecifications = ({
  items,
}: ProductDescriptionTechnicalSpecificationsProps) => {
  const visibleItems = items?.filter((item) => !item.hide)

  if (!visibleItems || visibleItems.length === 0) {
    return null
  }

  return (
    <div className='mt-6 rounded-2xl bg-light p-4 px-5 md:px-10 lg:mt-10'>
      <Accordion
        type='multiple'
        className='w-full'
        defaultValue={(() => {
          const firstIndex = visibleItems.findIndex(
            (item) => item.description && item.description.trim() !== ''
          )
          return firstIndex !== -1 ? [`item-${firstIndex}`] : []
        })()}
      >
        {visibleItems.map((item, index) => {
          const hasDescription =
            item.description && item.description.trim() !== ''

          if (!hasDescription) {
            // Nếu không có description, chỉ hiển thị title và icon không có accordion
            return (
              <div
                key={`technical-specification-${index}`}
                className='border-b border-neutral-200 py-4 last:border-b-0'
              >
                <div className='flex items-center gap-4'>
                  {item.logo && (
                    <div className='flex-shrink-0'>
                      <Image
                        src={ImageHelper.getN7ImageUrl(item.logo)}
                        alt={item.title || ''}
                        width={40}
                        height={40}
                        className='h-10 w-10 object-contain'
                        unoptimized
                      />
                    </div>
                  )}
                  <span className='flex-1 text-left font-sans text-xl text-neutral-900 lg:text-2xl'>
                    {item.title}
                  </span>
                </div>
              </div>
            )
          }

          // Nếu có description, hiển thị với accordion
          return (
            <AccordionItem
              key={`technical-specification-${index}`}
              value={`item-${index}`}
              className='border-b border-neutral-200 last:border-b-0'
            >
              <AccordionTrigger
                hasPlusIcon
                className='py-2 hover:no-underline md:py-8'
              >
                <div className='flex flex-1 items-center gap-4'>
                  {item.logo && (
                    <div className='flex-shrink-0'>
                      <Image
                        src={ImageHelper.getN7ImageUrl(item.logo, 40)}
                        alt={item.title || ''}
                        width={40}
                        height={40}
                        className='h-5 w-5 object-contain'
                        unoptimized
                      />
                    </div>
                  )}
                  <span className='flex-1 text-left font-sans text-xl text-neutral-900 lg:text-2xl'>
                    {item.title}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className='pb-4 pt-0 md:max-w-[70%]'>
                <div className='lg:ml-2 lg:mr-6'>
                  <div
                    className='text-sm leading-relaxed text-neutral-600 md:text-base [&_img]:!inline [&_img]:!align-middle [&_ul]:!list-disc [&_ul]:!pl-6'
                    dangerouslySetInnerHTML={{
                      __html: sanitize(item.description),
                    }}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          )
        })}
      </Accordion>
    </div>
  )
}
